import { Component, OnChanges, SimpleChanges } from '@angular/core';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { ListingPublic, ListingVariationPublic } from '../../../../globalTypes/listings.types';
import { lastValueFrom, take, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ListingService } from '../../services/listing.service';
import { listingsFeature, ListingsPageActions } from '../../store/listings.store';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ButtonComponent } from '../../component/layout/element/button.component';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Logger } from '../../helper/logger';
import { PriceStockVariationComponent } from './price-stock.variation.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonPipe } from '@angular/common';
import { UploadFile } from '../../file/upload-file';
import { DigitalFolderPublic, DigitalItemPublic, createEmptyDigitalFolder } from '../../../../globalTypes/file.types';
import { FileTreeComponent } from "../../file/file-tree";
// import { UiErrorSnackComponent } from '../../helper/error.handling';
// import { errorMsgConfig } from '../../../../globalTypes/base.types';
// import { CommonErrorComponent } from '../../component/error/common-error.component';
const log = new Logger('PriceStockComponent');

const digitsOnlyRegex = /^\d+$/;
@Component({
  selector: 'msh-price-stock',
  imports: [MatButtonModule, PriceStockVariationComponent, MatSlideToggleModule, FormsModule, MatSelectModule, ButtonComponent, FileTreeComponent],
  template: `
  <mat-slide-toggle class="mb-7" [checked]="listing && listing.listingVariations_on_listing && listing.listingVariations_on_listing.length > 1" (change)="toggleVariations($event)"> Listing has more than one variation</mat-slide-toggle>
  @if(isDigital){
    <msh-file-tree
      [folders]="folders"
      [files]="files"
      [locationId]="listing?.locationId || ''"
      [listingId]="listing?.id || ''"
      (onFoldersChange)="foldersChanged($event)"
      (onFilesChange)="filesChanged($event)"
    ></msh-file-tree>
  }
  @if(listing){
    @if(listing.listingVariations_on_listing && listing.listingVariations_on_listing.length > 0){
      @for(variation of listing.listingVariations_on_listing; track variation.id; let variationIndex = $index){
        @if(variationIndex > 0){<div class="h-8"></div>}
        <msh-price-stock-variation
          [isOnlyVariation]="listing.listingVariations_on_listing.length === 1"
          [v]="variation"
          [isDigital]="isDigital"
          [selectableFolders]="selectableFolders"
          [totalVariations]="listing.listingVariations_on_listing.length"
          [images]="listing.listingImages_on_listing || []"
          (onVariantChange)="onVariantChange(variation, variationIndex, $event)"
          (onDelete)="onVariantDelete(variationIndex)"
        ></msh-price-stock-variation>
      }
      <button mat-button class="mb-7 mx-auto" (click)="addVariant()" color="accent">+ Add Variation</button>
    }
  }
  <div class="grow"></div>
  <msh-button class="mb-14" (onClick)="onSubmitClick($event)"
    [disabled]="!this.hasPriceAndStock"
    [isLoading]="isLoading" [cta]="cta"></msh-button>
  `,
  styles: `:host { display: flex; flex-direction: column; flex-grow: 1; height: 100%;}`
})
export class PriceStockComponent extends Base<PriceStockComponent> implements OnChanges {
  listing: ListingPublic | Partial<ListingPublic> | null = null;
  hasPriceAndStock = false;
  cta = 'Update';
  isLoading = false;
  isDigital = false;
  folders: DigitalFolderPublic[] = [];
  selectableFolders: DigitalFolderPublic[] = [];
  files: DigitalItemPublic[] = [];
  hasVariations = false;
  slicedVariations: Partial<ListingVariationPublic>[] = [];

  constructor(
    private router: Router,
    private store: Store,
    private snackbar: MatSnackBar,
    private listingService: ListingService,
  ){ super();
    this.store.select(listingsFeature.selectListing).pipe(takeUntil(this.$destroy)).subscribe(listing=>{
      if(!listing) return; 
      this.listing = JSON.parse(JSON.stringify(listing));
      if(!this.listing || !this.listing?.id || this.listing.id === 'new') {
        this.cta = 'Next';
      }
      this.isDigital = this.listing && this.listing.category && this.listing.category.isDigital || false;
      this.hasVariations = !!this.listing && !!this.listing.listingVariations_on_listing
        && this.listing.listingVariations_on_listing.length > 1
      this.setValidation();
      this.hasVariations = !!this.listing && !!this.listing.listingVariations_on_listing
        && this.listing.listingVariations_on_listing.length > 1;
      if(this.isDigital){
        if(this.listing && this.listing.digitalFolders_on_listing && this.listing.digitalFolders_on_listing.length > 0){
          this.folders = this.listing.digitalFolders_on_listing;
          this.files = this.listing.digitalFolders_on_listing.flatMap(folder => folder.digitalItems_on_folder.map(item => ({...item, parentId: folder.id})) || []);
        }else{
          this.folders = [createEmptyDigitalFolder()];
          this.files = [];
        }
        this.foldersChanged(this.folders);
        this.assignDefaultFolderToVariations()
      }
    })
  }

  foldersChanged(folders: DigitalFolderPublic[]){
    this.selectableFolders = this.folders.filter(folder => folder.parentId !== null);
    if(this.listing && this.listing.digitalFolders_on_listing){
      this.listing.digitalFolders_on_listing = folders;
    }
  }

  filesChanged(files: DigitalItemPublic[]){
    this.files = files;
    this.setValidation();
  }

  assignDefaultFolderToVariations(){
    if(!this.isDigital || !this.listing || !this.listing.listingVariations_on_listing || !this.folders || this.folders.length === 0) return;
    const defaultFolderId = this.folders[0].id;
    this.listing.listingVariations_on_listing.forEach(variation => {
      if(!variation.digitalFolderListingVariations_on_listingVariation || variation.digitalFolderListingVariations_on_listingVariation.length === 0){
        variation.digitalFolderListingVariations_on_listingVariation = [{digitalFolderId: defaultFolderId, displayOrder: 0}];
      }
    });
  }

  async ngOnInit(): Promise<void> {
    if(this.listing && this.listing.id === 'new'){
      this.setValidation();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['listing']){
      log.log({listing: this.listing})
      
    }
  }

  toggleVariations(ev:MatSlideToggleChange){
    if(!this.listing) return;
    if(ev.checked){
      if(this.slicedVariations.length > 0 && !!this.listing.listingVariations_on_listing){
        this.listing.listingVariations_on_listing.push(...this.slicedVariations);
      }else{
        this.addVariant();
      }
    } else if(this.listing && this.listing.listingVariations_on_listing && this.listing.listingVariations_on_listing[0]) {
      this.slicedVariations = this.listing.listingVariations_on_listing?.slice(1) || [];
      this.listing.listingVariations_on_listing = [this.listing.listingVariations_on_listing[0]];
    }
    this.setValidation();
  }

  addVariant(){
    if(!this.listing) return;
    this.hasVariations = true;
    if(!this.listing.listingVariations_on_listing) this.listing.listingVariations_on_listing = [];
    const variantsLength = this.listing.listingVariations_on_listing?.length || 0;
    const prevVariant = variantsLength > 0 ? this.listing.listingVariations_on_listing [variantsLength -1] : undefined;
    const newVariant = this.listingService.createBlankListingVariation() as Partial<ListingVariationPublic>
    if(prevVariant){
      newVariant.name = prevVariant.name;
      newVariant.description = prevVariant.description;
      newVariant.priceInCents = prevVariant.priceInCents;
      newVariant.quantity = prevVariant.quantity;
    }
    this.listing.listingVariations_on_listing.push(newVariant);
    this.assignDefaultFolderToVariations();
    this.setValidation();
  }

  onVariantChange(variant: Partial<ListingPublic>, idx: number, ev: any){
    if(this.listing && this.listing.listingVariations_on_listing){
      this.listing.listingVariations_on_listing[idx] = variant;
    }
    log.log({variants: this.listing?.listingVariations_on_listing})
    this.setValidation();
  }

  setValidation(){
    this.hasPriceAndStock = this.listing?.listingVariations_on_listing?.every(
      (v,idx) => (
        v.priceInCents !== undefined 
        && digitsOnlyRegex.test(v.priceInCents+'') 
        && ( (this.isDigital && this.files.length > 0)
          || v.quantity !== undefined && v.quantity !== null)) 
        && (idx === 0 || v.name)
    ) || false;
  }

  onVariantDelete(variationIndex: number){
    if(!this.listing || !this.listing.listingVariations_on_listing) return;
    if(variationIndex === -1) return;
    this.listing.listingVariations_on_listing?.splice(variationIndex, 1);
    if(this.listing.id !== "new"){
      this.snackbar.open("Deletion will only be finalized on update", "close", {duration: 8000})
    }
    this.setValidation();
  }

  placeFoldersAndFilesInListing(){
    if(!this.listing) return;
    this.listing.digitalFolders_on_listing = this.folders;
    this.listing.digitalFolders_on_listing?.forEach(folder => {
      folder.digitalItems_on_folder = this.files.filter(file => file.parentId === folder.id);
    });
  }

  async onSubmitClick(ev: Event){
    if(!this.listing) return;
    // TODO test what happens if you don't name a single variation
    // if(this.listing.listingVariations_on_listing?.length === 1) {
    //   this.listing.listingVariations_on_listing[0].name = this.listing.name;
    //   this.listing.listingVariations_on_listing[0].description = this.listing.description;
    // }
    this.isLoading = true; 
    let lowestPrice = Infinity;
    this.listing.listingVariations_on_listing?.forEach(variation => { lowestPrice = Math.min(lowestPrice, (variation.priceInCents ? variation.priceInCents : Infinity))});
    console.log({lowestPrice})
    if(lowestPrice !== Infinity) this.listing.lowestPrice = lowestPrice;
    this.assignDefaultFolderToVariations();
    this.listing.searchContents = this.listingService.compileSearchContents(this.listing)
    this.placeFoldersAndFilesInListing();
    if(this.listing?.id === "new"){
      console.log({listing: this.listing, folders: this.folders, files: this.files})
      this.store.dispatch(ListingsPageActions.progressListing({listing: this.listing as Partial<ListingPublic>}))
      const nextPage = this.listing.name ? 'delivery' : 'details';
      this.router.navigate(['manage-listing', nextPage, this.listing.id])
    } else {
      this.isLoading = true;
      const originalListing = await lastValueFrom(this.store.select(listingsFeature.selectListing).pipe(take(1)))
      const originalVariationIds: string[] = originalListing?.listingVariations_on_listing?.map(v => v.id || '') || [];
      const currentVariationIds = this.listing.listingVariations_on_listing?.map(v => v.id) || [];
      const deletedVariationIds = originalVariationIds.filter(id => id && !currentVariationIds.includes(id));
      if(deletedVariationIds.length > 0){
        await Promise.all(deletedVariationIds.map(id => this.listingService.deleteListingVariation(id, this.listing?.locationId, this.listing?.id)))
      }
      await this.listingService.saveListing(this.listing);
      this.snackbar.open("Listing price & stock have been saved", "close", {duration: 8000})
      this.isLoading = false;
    }
  }
}
